import React, { useState, useEffect } from "react";
import { Modal, Button, Alert, Spinner, Input, Label } from "../ui";
import api from "../services/apiClient";
import AuthService from "../services/authentication";

function PatientEditModal({ show, onHide, patient, onPatientUpdated }) {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
  });

  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  const auth = AuthService.useAuth();

  // Initialize form data when patient changes
  useEffect(() => {
    if (patient) {
      setFormData({
        first_name: patient.patient_first_name || "",
        last_name: patient.patient_last_name || "",
        email: patient.patient_email || "",
      });
    }
  }, [patient]);

  const handleInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));

    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      const response = await api.put(
        `/api/practitioner/patient/${patient.id}`,
        formData,
        { headers: auth.authHeader() }
      );

      if (response.data.patient) {
        console.log("API response:", response.data.patient);
        onPatientUpdated(response.data.patient);
        onHide();
      }
    } catch (error) {
      console.error("Error updating patient:", error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors([
          "An error occurred while updating the patient. Please try again.",
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setErrors([]);
    if (patient) {
      setFormData({
        first_name: patient.patient_first_name || "",
        last_name: patient.patient_last_name || "",
        email: patient.patient_email || "",
      });
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="md">
      <Modal.Header closeButton>
        <Modal.Title>Edit Patient Details</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errors.length > 0 && (
          <Alert variant="danger">
            <ul className="mb-0">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
            <div>
              <Label>First Name *</Label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) =>
                  handleInputChange("first_name", e.target.value)
                }
                required
              />
            </div>
            <div>
              <Label>Last Name *</Label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) =>
                  handleInputChange("last_name", e.target.value)
                }
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <Label>Email Address *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              required
            />
            <p className="text-sm text-gray-500">
              This email address is used to send prescription links to the
              patient. Existing prescriptions will be updated with this address.
            </p>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving...
            </>
          ) : (
            "Save Changes"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PatientEditModal;
